// A product page renders two of the many sections that include this file, so the browser evaluates
// it twice. Wrapped to keep the declarations file-local — as globals, the second run threw.
(function () {
  // How long the "Added to bag" toast stays up. The "+" button itself shows no success
  // state — it goes plus -> spinner -> plus — so this toast is the only confirmation.
  const QUICK_ADD_TOAST_DURATION = 2500;

  // Minimum time the spinner is held before the toast appears. cart/add.js often resolves in
  // ~300ms, which is faster than the spinner's own 0.5s cross-fade — without this the toast
  // pops while the spinner is still fading in mid-rotation, and the loading state never reads
  // as a completed action. Roughly half a rotation of the 1.4s spinner.
  const QUICK_ADD_MIN_SPINNER_DURATION = 600;

  // Matches the .QuickAdd__icon / .QuickAdd__loading-spinner opacity transition in
  // snippets/product-card-quick-add.liquid, so the toast lands as the plus finishes
  // returning rather than on top of a spinner that is still turning.
  const QUICK_ADD_SPINNER_EXIT_DURATION = 200;

  // Shared, not per-instance: one toast serves the whole grid, so clicking tile B has to take
  // down both A's toast and A's dismiss timer.
  let quickAddToastTimeout = null;
  let quickAddToastElement = null;

  // Matches the .QuickAddToast transition; the clone is detached once the fade has played.
  const QUICK_ADD_TOAST_FADE_DURATION = 250;

  function removeQuickAddToast() {
    if (!quickAddToastElement) return;
    quickAddToastElement.remove();
    quickAddToastElement = null;
  }

  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        constructor() {
          super();

          this.form = this.querySelector('form');
          this.variantIdInput.disabled = false;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          this.cart =
            document.querySelector('cart-notification') ||
            document.querySelector('cart-drawer') ||
            document.querySelector('cart-modal');
          this.submitButton = this.querySelector('[type="submit"]');
          this.submitButtonText = this.submitButton.querySelector('span');

          // data-quiet-add forms never open a dialog — cart-modal.js / cart-drawer.js return
          // before open() — so advertising aria-haspopup="dialog" on them misinforms
          // assistive technology.
          if (
            !this.hasAttribute('data-quiet-add') &&
            (document.querySelector('cart-drawer') || document.querySelector('cart-modal'))
          ) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
          }

          this.hideErrors = this.dataset.hideErrors === 'true';
        }

        /**
         * Quick-add "+" buttons confirm a successful add with the "Added to bag" toast
         * instead of any button-level success state. Everything else (PDP add to cart,
         * configurable products, cart upsells) keeps the existing loading-only behaviour.
         * Gated on data-quick-add-feedback rather than data-quick-add-atc so cart upsell
         * tiles — which share the latter — are untouched.
         * @returns {boolean}
         */
        get isQuickAddFeedback() {
          return !!this.submitButton && this.submitButton.hasAttribute('data-quick-add-feedback');
        }

        /**
         * Clones the toast template into the clicked card, so the browser keeps it pinned
         * there while the grid scrolls.
         * @param {'added'|'error'} state
         */
        showQuickAddToast(state) {
          // isConnected: the card can be torn out during the deferred call below, which would
          // append the toast into a detached node and strand it as the active one.
          if (!this.isQuickAddFeedback || !this.isConnected) return;

          const template = document.querySelector('[data-quick-add-toast-template]');
          if (!template) return;

          // The image container is the square that reads as "the card"; fall back to the whole
          // tile, then to the viewport.
          const card =
            this.closest('.ProductVariantTile__image-container') || this.closest('.ProductVariantTile');

          clearTimeout(quickAddToastTimeout);
          removeQuickAddToast();

          const toast = template.content.firstElementChild.cloneNode(true);
          const message = state === 'error' ? toast.dataset.errorMessage : toast.dataset.addedMessage;

          // Set before attaching: a width change after the reveal begins gets animated by the
          // centring translate, which reads as the toast sliding into place.
          toast.querySelector('[data-quick-add-toast-text]').textContent = message || '';
          toast.classList.toggle('is-error', state === 'error');
          toast.classList.toggle('is-detached', !card);
          (card || document.body).appendChild(toast);
          quickAddToastElement = toast;

          // Flush layout so the transition has a start state; a class set in the same task as
          // the insertion, with no reflow between, just snaps.
          void toast.offsetWidth;
          toast.classList.add('is-visible');

          // Blank then re-set so an unchanged message re-announces — role="status" only fires
          // on content change. Off-screen span, so the toast's own layout is untouched.
          const announcer = document.querySelector('[data-quick-add-toast-announcement]');
          if (announcer) {
            announcer.textContent = '';
            requestAnimationFrame(() => {
              announcer.textContent = message || '';
            });
          }

          quickAddToastTimeout = setTimeout(() => {
            toast.classList.remove('is-visible');
            quickAddToastTimeout = null;
            // Guarded: a new add during the fade already replaced this toast.
            setTimeout(() => {
              if (quickAddToastElement === toast) removeQuickAddToast();
            }, QUICK_ADD_TOAST_FADE_DURATION);
          }, QUICK_ADD_TOAST_DURATION);
        }

        async onSubmitHandler(evt) {
          evt.preventDefault();
          if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

          this.handleErrorMessage();

          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          const loadingStartedAt = performance.now();
          const loading_spinner = this.querySelector('.loading__spinner');
          if (loading_spinner) loading_spinner.classList.remove('hidden');

          const formData = {};
          const addOns = this.form.querySelectorAll('input.js-add-on-input[name$="[id]"]');
          const addOnQuantities = this.form.querySelectorAll('input.js-add-on-input[name$="[quantity]"]');
          const variantId = this.form.querySelector('[data-main-variant-id]')?.value;
          const variantSku = this.form.querySelector('[data-main-variant-sku]')?.value;
          const quantity = this.form.querySelector('[name="quantity"]')?.value;
          const sellingPlan = this.form.querySelector('[data-selling-plan-id]')?.value;
          const properties = document.querySelectorAll('product-customization input[name^="properties"]');

          // Build properties object from personalization form inputs
          const customizationProps = {};
          properties.forEach((property) => {
            // Extract property name from brackets (e.g., "properties[_personalization]" -> "_personalization")
            const match = property.name.match(/properties\[(.+)\]/);
            if (match && property.value) {
              customizationProps[match[1]] = property.value;
            }
          });

          // Construct form data items in reverse order to ensure the correct order of items in cart
          formData.items = [
            ...Array.from(addOns)
              .reverse()
              .map((addOn, i) => {
                return {
                  id: addOn.value,
                  quantity: addOnQuantities[i].value,
                };
              }),
            {
              id: variantId,
              quantity: quantity || 1,
              ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
              properties: customizationProps,
            },
          ];

          if (this.cart) {
            formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
            formData.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(formData);

          try {
            const data = await fetch(window.Shopify.routes.root + 'cart/add.js', config);
            const response = await data.json();

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.showQuickAddToast('error');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              });
            this.error = false;

            // Sequence the hand-off: spinner completes its beat, resolves back to the plus,
            // and only then does the toast confirm.
            //
            // The add is already done at this point — everything below (renderContents,
            // promotion handling) is background work that can run for seconds on a slow
            // connection. So the button is released here rather than in `finally`, which
            // would otherwise hold the spinner for the whole cart re-render. `finally`
            // removes the class again; that is idempotent.
            if (this.isQuickAddFeedback) {
              const spinnerElapsed = performance.now() - loadingStartedAt;
              if (spinnerElapsed < QUICK_ADD_MIN_SPINNER_DURATION) {
                await new Promise((resolve) => setTimeout(resolve, QUICK_ADD_MIN_SPINNER_DURATION - spinnerElapsed));
              }
              this.submitButton.classList.remove('loading');
              // setTimeout, not await: the toast must not delay the cart re-render below.
              setTimeout(() => this.showQuickAddToast('added'), QUICK_ADD_SPINNER_EXIT_DURATION);
            }
            const quickAddModal = this.closest('quick-add-modal');
            const byPassSliderCart = this.hasAttribute('data-bypass-slider-cart');
            const quietAdd = this.hasAttribute('data-quiet-add');
            const promotionContainer = document.querySelector('promotion-container');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response, byPassSliderCart, quietAdd);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (promotionContainer) {
              const success = await promotionContainer.handlePromotion();
              if (!success) {
                this.cart.renderContents(response, byPassSliderCart, quietAdd);
              }
            } else {
              this.cart.renderContents(response, byPassSliderCart, quietAdd);
            }
          } catch (e) {
            console.error(e);
            if (this.isQuickAddFeedback) {
              // A quick add lives inside a collection grid or the Product Info modal.
              // Redirecting to /cart destroys that context for what is usually a transient
              // network failure — show the error toast and let the shopper retry in place.
              this.showQuickAddToast('error');
              return;
            }
            window.location = window.routes.cart_url;
          } finally {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            const loading_spinner = this.querySelector('.loading__spinner');
            if (loading_spinner) loading_spinner.classList.add('hidden');

            // after cart is updated, check if the express payment options have changed
            const expressPaymentWrapper = document.querySelector('.cart__dynamic-checkout-buttons');
            if (expressPaymentWrapper) {
              const observer = new MutationObserver(() => {
                const expressPaymentOptions = document.querySelector('shopify-accelerated-checkout-cart')?.children
                  ?.length;
                if (document.body.classList.contains('country-code-us')) {
                  document.body.setAttribute('data-payment-options', expressPaymentOptions - 1);
                } else {
                  document.body.setAttribute('data-payment-options', expressPaymentOptions);
                }
                observer.disconnect();
              });
              observer.observe(expressPaymentWrapper, { childList: true, subtree: true });
            }
          }
        }

        handleErrorMessage(errorMessage = false) {
          if (this.hideErrors) return;

          this.errorMessageWrapper =
            this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
          if (!this.errorMessageWrapper) return;
          this.errorMessage =
            this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

          if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
          }
        }

        toggleSubmitButton(disable = true, text) {
          if (disable) {
            if (text) this.submitButtonText.textContent = text;
            if (text === window.variantStrings.soldOut) {
              const product_submit_buttons = document.querySelectorAll('.product-submit-button');
              product_submit_buttons.forEach((button) => {
                button.classList.add('disabled:!tw-bg-gray');
                button.classList.add('disabled:!tw-border-gray');
              });
            }
            this.submitButton.setAttribute('disabled', 'disabled');
          } else {
            const product_submit_buttons = document.querySelectorAll('.product-submit-button');
            product_submit_buttons.forEach((button) => {
              button.classList.remove('disabled:!tw-bg-gray');
              button.classList.remove('disabled:!tw-border-gray');
            });
            this.submitButtonText.textContent = window.variantStrings.addToCart;
            this.submitButton.removeAttribute('disabled');
          }
        }

        get variantIdInput() {
          return this.form.querySelector('[name=id]');
        }
      }
    );
  }
})();
